// features/projects/templates/continuum/dashboard.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM DASHBOARD — synthesis card grid
//
// Answers "where am I + what to do next" in 30 seconds, with
// enough narrative depth that the user actually changes
// behavior — not just a summary view.
//
// Two design rules locked with Tim:
//
// 1. Two-lens honesty (2026-05-03): every card serves the
//    researcher self-auditing AND any reader judging the work.
//    Caveats are first-class card content.
//
// 2. 5-layer cards (2026-05-03): every card carries Read +
//    Story + Takeaway + Action + Caveat. Summary-stat-only
//    cards undershoot — the pattern emerged after Tim flagged
//    that count cards weren't telling the whole story.
//
// Card roadmap (per the revised 7-card contract):
//   1. Where the research stands       ← shipped here
//   2. The strongest threads           ← shipped here
//   3. Where things break              ← next commit
//   4. The path forward                ← next commit
//   5. What we still don't know        ← next commit
//   6. Solution chains                 ← needs analytical helper
//   7. Track record (calibration)      ← next commit
//
// The Signal-themed mocked dashboard is preserved at
// dashboard-legacy-signal.js for visual reference.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import { C, FONT as F } from './tokens';
import { DYNAMICS } from '../../../../lib/continuum/simulation/methodology';
import { findSolutionChains } from '../../../../lib/continuum/research/solution-chains';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

const VERDICT_COLOR = {
  PARALLEL:        C.sage,
  'NEAR-PARALLEL': C.gold,
  DIVERGENT:       C.terra,
  insufficient_data: C.amber,
  pending:         C.dim,
  error:           C.terra,
};

const PHASE_A_COLOR = {
  survived:              C.sage,
  'survived-respecified': C.gold,
  killed:                C.terra,
};

const PHASE_A_LABEL = {
  survived:              'survived',
  'survived-respecified': 'survived · respecified',
  killed:                'killed',
};

export default function ContinuumDashboard({ workspaceId, onNavigate }) {
  const [data, setData] = useState({
    comparisons: null, hypotheses: null, predictions: null, questions: null,
    error: null,
  });

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const [compRes, hypRes, predRes, qRes] = await Promise.all([
        fetch(`/api/continuum/comparisons?workspace_id=${workspaceId}`),
        fetch(`/api/continuum/hypotheses/list?workspace_id=${workspaceId}`),
        fetch(`/api/continuum/predictions/list?workspace_id=${workspaceId}`),
        fetch(`/api/continuum/questions/list?workspace_id=${workspaceId}`),
      ]);
      const [compJ, hypJ, predJ, qJ] = await Promise.all([
        compRes.ok ? compRes.json() : { comparisons: [] },
        hypRes.ok  ? hypRes.json()  : { hypotheses: [] },
        predRes.ok ? predRes.json() : { predictions: [] },
        qRes.ok    ? qRes.json()    : { questions: [] },
      ]);
      setData({
        comparisons: compJ.comparisons || [],
        hypotheses: hypJ.hypotheses || [],
        predictions: predJ.predictions || [],
        questions: qJ.questions || [],
        error: null,
      });
    } catch (err) {
      setData(d => ({ ...d, error: err.message }));
    }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  const loading = data.comparisons === null;

  return (
    <div style={{
      height: '100%', background: C.bg, color: C.text,
      fontFamily: F.mono, fontSize: 12,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <Header loading={loading} onReload={reload} />
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '20px 22px' }}>
        {loading ? (
          <Empty msg="Loading research state…" />
        ) : data.error ? (
          <Empty msg={`Error: ${data.error}`} tone="error" />
        ) : (
          <CardGrid data={data} onNavigate={onNavigate} />
        )}
      </div>
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────
function Header({ loading, onReload }) {
  const accent = C.gold;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '0 20px', height: 42,
      borderBottom: `1px solid ${C.b1}`, background: C.s1, flexShrink: 0, gap: 14,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 9, padding: '3px 10px', borderRadius: 4,
        background: 'rgba(200,169,110,.10)', border: '1px solid rgba(200,169,110,.25)',
        color: accent,
      }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: accent }} />
        Dashboard — Research State
      </div>
      <div style={{ fontSize: 9, color: C.dim }}>
        Where you are · what to do next · what's missing
      </div>
      <div style={{ marginLeft: 'auto' }}>
        <button
          onClick={onReload}
          disabled={loading}
          style={{
            fontSize: 9, padding: '5px 14px', borderRadius: 4,
            background: 'transparent', border: `1px solid ${C.b2}`,
            color: C.static, cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: F.mono, letterSpacing: '0.06em',
          }}
        >
          {loading ? '⟳ Loading' : '↻ Refresh'}
        </button>
      </div>
    </div>
  );
}

function Empty({ msg, tone }) {
  return (
    <div style={{
      padding: '60px 20px', textAlign: 'center',
      color: tone === 'error' ? C.terra : C.dim,
      fontFamily: F.sans, fontSize: 13, lineHeight: 1.6,
    }}>{msg}</div>
  );
}

// ─── Card grid ────────────────────────────────────────────────
function CardGrid({ data, onNavigate }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 1280 }}>
      <WhereTheResearchStandsCard data={data} onNavigate={onNavigate} />
      <StrongestThreadsCard data={data} onNavigate={onNavigate} />
      <WhereThingsBreakCard data={data} onNavigate={onNavigate} />
      <PathForwardCard data={data} onNavigate={onNavigate} />
      <WhatWeDontKnowCard data={data} onNavigate={onNavigate} />
      <SolutionChainsCard data={data} onNavigate={onNavigate} />
      <TrackRecordCard data={data} onNavigate={onNavigate} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CARD 1 — WHERE THE RESEARCH STANDS
// ═══════════════════════════════════════════════════════════
// Q: "What's the state of my research right now, and what does
//     that state mean?"
// 5 layers: verdict mix + Phase A coverage (read), heterogeneity
// narrative (story), the meta-pattern of "which mechanisms
// transfer" (takeaway), the highest-leverage next move (action),
// the per-finding caveat about what's not yet stress-tested.
function WhereTheResearchStandsCard({ data, onNavigate }) {
  const comps = data.comparisons || [];
  const total = comps.length;

  const buckets = useMemo(() => groupByVerdict(comps), [comps]);
  const segments = [
    { key: 'PARALLEL',          label: 'parallel',      count: buckets.PARALLEL.length,         color: VERDICT_COLOR.PARALLEL },
    { key: 'NEAR-PARALLEL',     label: 'near-parallel', count: buckets['NEAR-PARALLEL'].length, color: VERDICT_COLOR['NEAR-PARALLEL'] },
    { key: 'DIVERGENT',         label: 'divergent',     count: buckets.DIVERGENT.length,        color: VERDICT_COLOR.DIVERGENT },
    { key: 'pending',           label: 'pending',       count: buckets.pending.length + buckets.insufficient_data.length, color: VERDICT_COLOR.pending },
  ];
  const visible = segments.filter(s => s.count > 0);

  const substantive = comps.filter(isSubstantive);
  const phaseACovered = substantive.filter(c => c.phase_a_status).length;

  const story = composeStandsStory(buckets, substantive);
  const takeaway = composeStandsTakeaway(buckets, substantive);
  const action = composeStandsAction(comps, substantive, phaseACovered);
  const caveat = composeStandsCaveat(substantive, phaseACovered);

  return (
    <Card label="Where the research stands" onClick={() => onNavigate?.('findings')}>
      {/* READ */}
      <Layer>
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 14,
          flexWrap: 'wrap', marginBottom: 10,
        }}>
          <div style={{
            fontFamily: F.mono, fontSize: 28, color: C.text,
            fontWeight: 300, letterSpacing: '-0.01em',
          }}>
            {total}<span style={{ fontSize: 13, color: C.dim, fontWeight: 400, marginLeft: 6 }}>findings</span>
          </div>
          <div style={{ fontSize: 11, color: C.mid, fontFamily: F.sans }}>
            {substantive.length} substantive · {phaseACovered} red-teamed
          </div>
        </div>
        {visible.length > 0 && (
          <div style={{
            display: 'flex', height: 10, borderRadius: 3, overflow: 'hidden',
            border: `1px solid ${C.b1}`, marginBottom: 8,
          }}>
            {visible.map(s => (
              <div key={s.key} title={`${s.count} ${s.label}`}
                   style={{ flex: s.count, background: s.color, opacity: 0.85 }} />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontFamily: F.mono, fontSize: 10 }}>
          {visible.map(s => (
            <span key={s.key} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              color: s.color, letterSpacing: '0.04em',
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.color }} />
              {s.count} {s.label}
            </span>
          ))}
        </div>
      </Layer>

      {/* STORY */}
      <LayerSection label="Story">{story}</LayerSection>

      {/* TAKEAWAY */}
      <LayerSection label="Takeaway" accent={C.gold}>{takeaway}</LayerSection>

      {/* ACTION */}
      {action && (
        <LayerAction
          accent={C.sage}
          label={action.label}
          why={action.why}
          onClick={(e) => { e.stopPropagation(); onNavigate?.(action.target); }}
        />
      )}

      {/* CAVEAT */}
      {caveat && <CaveatStrip text={caveat} />}
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════
// CARD 2 — THE STRONGEST THREADS
// ═══════════════════════════════════════════════════════════
// Q: "Which findings are most defensible right now, and what
//     story does each one tell?"
// Renders the top 3 substantive findings ranked by Phase A
// status + confidence. Each thread carries its own per-finding
// 5 layers: stat read, deviation point, takeaway, intervention
// available, and prediction-tied caveat.
function StrongestThreadsCard({ data, onNavigate }) {
  const comps = data.comparisons || [];
  const hypotheses = data.hypotheses || [];
  const predictions = data.predictions || [];
  const threads = useMemo(
    () => pickStrongestThreads(comps, hypotheses, predictions, 3),
    [comps, hypotheses, predictions],
  );

  if (threads.length === 0) {
    return (
      <Card label="The strongest threads">
        <Layer>
          <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.5, fontFamily: F.sans }}>
            No substantive findings yet. Threads will surface here as findings receive verdicts.
          </div>
        </Layer>
      </Card>
    );
  }

  return (
    <Card label={`The strongest threads (${threads.length})`}>
      <Layer>
        <div style={{ fontSize: 11, color: C.mid, fontFamily: F.sans, lineHeight: 1.5 }}>
          Ranked by Phase A status + confidence + sample size. Each thread shows its
          deviation point — where the system departed from the expected baseline — plus
          what intervention the methodology suggests and which prediction tests it.
        </div>
      </Layer>

      <div style={{
        display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14,
      }}>
        {threads.map(t => (
          <ThreadRow key={t.comp.id} thread={t} onNavigate={onNavigate} />
        ))}
      </div>
    </Card>
  );
}

function ThreadRow({ thread, onNavigate }) {
  const { comp, dyn, hypothesis, prediction, deviationPoint, takeaway, intervention } = thread;
  const verdict = (comp.verdict || '').toUpperCase();
  const verdictColor = VERDICT_COLOR[verdict] || C.dim;
  const phaseAStatus = comp.phase_a_status;
  const phaseAColor = phaseAStatus ? PHASE_A_COLOR[phaseAStatus] : C.dim;
  const dynLabel = dyn?.label || comp.dynamic_key;

  return (
    <div style={{
      padding: '12px 14px',
      background: 'rgba(255,255,255,.02)',
      border: `1px solid ${C.b1}`,
      borderRadius: 5,
    }}>
      {/* Top strip: verdict pill, phase-a pill, dynamic name, stats */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        marginBottom: 10, fontFamily: F.mono, fontSize: 9,
      }}>
        <Pill color={verdictColor} text={verdict || 'PENDING'} />
        {phaseAStatus && (
          <Pill color={phaseAColor} text={`PHASE A · ${PHASE_A_LABEL[phaseAStatus]}`} />
        )}
        <span style={{ color: C.text, fontFamily: F.sans, fontSize: 12, fontWeight: 500 }}>
          {dynLabel}
        </span>
        <span style={{ marginLeft: 'auto', color: C.dim, letterSpacing: '0.04em' }}>
          eco {fmt(comp.eco_mean)} (n={comp.eco_n}) · human {fmt(comp.human_mean)} (n={comp.human_n})
          {comp.effect_size != null && ` · d=${fmt(comp.effect_size, 2)}`}
        </span>
      </div>

      {/* Deviation point — the inflection that produced the finding */}
      {deviationPoint && (
        <LayerSection label="Deviation point">{deviationPoint}</LayerSection>
      )}

      {/* Takeaway — the synthesis of what this finding means */}
      {takeaway && (
        <LayerSection label="Takeaway" accent={C.gold}>{takeaway}</LayerSection>
      )}

      {/* Intervention available — the prescription tied to this finding */}
      {intervention && (
        <LayerSection label="Intervention available" accent={C.sage}>
          {intervention}
        </LayerSection>
      )}

      {/* Linked prediction — the test that resolves this thread */}
      {prediction && (
        <LayerAction
          accent={C.purple || C.gold}
          label="Tested by forward prediction"
          why={`Resolves ${formatDate(prediction.resolution_date)} — ${truncate(prediction.claim, 100)}`}
          onClick={(e) => { e.stopPropagation(); onNavigate?.('predictions'); }}
        />
      )}

      {/* Caveat — the per-finding limit */}
      {thread.caveat && <CaveatStrip text={thread.caveat} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CARD 3 — WHERE THINGS BREAK
// ═══════════════════════════════════════════════════════════
// Q: "Which findings break the parallel claim, and what does
//     each break tell us?"
// Surfaces DIVERGENT findings + KILLED Phase A claims as
// constraint-shaped information. Breaks aren't failures —
// they sharpen what the methodology can credibly claim.
function WhereThingsBreakCard({ data, onNavigate }) {
  const comps = data.comparisons || [];
  const divergent = comps.filter(c => (c.verdict || '').toUpperCase() === 'DIVERGENT');
  const killed = comps.filter(c => c.phase_a_status === 'killed');
  const respecified = comps.filter(c => c.phase_a_status === 'survived-respecified');

  if (divergent.length === 0 && killed.length === 0 && respecified.length === 0) {
    return (
      <Card label="Where things break">
        <Layer>
          <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.55, fontFamily: F.sans }}>
            No breaks recorded yet. Either everything tested has held, or the test set is too small to surface domain-specificity. Both are interpretive risks worth tracking.
          </div>
        </Layer>
      </Card>
    );
  }

  const story = composeBreaksStory(divergent, killed, respecified);
  const takeaway = composeBreaksTakeaway(divergent, killed);
  const action = composeBreaksAction(divergent, killed);
  const caveat = composeBreaksCaveat(divergent);

  return (
    <Card label={`Where things break (${divergent.length + killed.length})`} onClick={() => onNavigate?.('findings')}>
      <Layer>
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap',
          marginBottom: 10,
        }}>
          {divergent.length > 0 && (
            <BreakStat color={VERDICT_COLOR.DIVERGENT} count={divergent.length} label="divergent" />
          )}
          {killed.length > 0 && (
            <BreakStat color={C.terra} count={killed.length} label="killed by Phase A" />
          )}
          {respecified.length > 0 && (
            <BreakStat color={PHASE_A_COLOR['survived-respecified']} count={respecified.length} label="respecified (claim narrowed)" />
          )}
        </div>
      </Layer>

      <LayerSection label="Story">{story}</LayerSection>
      <LayerSection label="Takeaway" accent={C.gold}>{takeaway}</LayerSection>

      {/* Per-break detail rows for the divergent set */}
      {divergent.length > 0 && (
        <div style={{
          marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${C.b1}`,
        }}>
          <div style={{
            fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
            color: C.dim, textTransform: 'uppercase', marginBottom: 8,
          }}>
            Per-break detail
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {divergent
              .slice()
              .sort((a, b) => Math.abs(Number(b.effect_size) || 0) - Math.abs(Number(a.effect_size) || 0))
              .map(c => <BreakRow key={c.id} comp={c} />)}
          </div>
        </div>
      )}

      {action && (
        <LayerAction
          accent={C.sage}
          label={action.label}
          why={action.why}
          onClick={(e) => { e.stopPropagation(); onNavigate?.(action.target); }}
        />
      )}

      {caveat && <CaveatStrip text={caveat} />}
    </Card>
  );
}

function BreakStat({ color, count, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{
        fontFamily: F.mono, fontSize: 22, color, fontWeight: 300,
        letterSpacing: '-0.01em',
      }}>{count}</span>
      <span style={{ fontSize: 11, color: C.mid, fontFamily: F.sans }}>{label}</span>
    </span>
  );
}

function BreakRow({ comp }) {
  const dyn = DYN_BY_KEY[comp.dynamic_key];
  const dynLabel = dyn?.label || comp.dynamic_key;
  const d = Number(comp.effect_size) || 0;
  const direction = d > 0 ? 'eco-side higher' : 'human-side higher';
  const interp = d > 0
    ? 'ecological systems show the larger ratio — likely capacity differences (biology, longer time series, less anticipation)'
    : 'human systems show the larger ratio — possible substrate-specific amplification or anticipatory dynamics';
  return (
    <div style={{
      padding: '8px 10px',
      background: 'rgba(196,80,80,.04)',
      border: `1px solid ${VERDICT_COLOR.DIVERGENT}30`,
      borderRadius: 4,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
        marginBottom: 4,
      }}>
        <span style={{ color: C.text, fontFamily: F.sans, fontSize: 12, fontWeight: 500 }}>
          {dynLabel}
        </span>
        <span style={{ fontFamily: F.mono, fontSize: 10, color: VERDICT_COLOR.DIVERGENT }}>
          d={fmt(comp.effect_size, 2)} · {direction}
        </span>
        {!comp.phase_a_status && (
          <span style={{
            fontFamily: F.mono, fontSize: 8, color: C.amber, letterSpacing: '0.06em',
            border: `1px solid ${C.amber}50`, padding: '1px 6px', borderRadius: 2,
            textTransform: 'uppercase',
          }}>
            Phase A not run
          </span>
        )}
      </div>
      <div style={{
        fontFamily: F.sans, fontSize: 11, color: C.mid, lineHeight: 1.5,
      }}>
        {interp}.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CARD 4 — THE PATH FORWARD
// ═══════════════════════════════════════════════════════════
// Q: "What are the 3 highest-leverage things I should do next?"
// Portfolio-wide ranking of next moves derived from the same
// rules engine the per-finding next-moves panel uses, but
// surfaced at the dashboard level so a researcher can decide
// where to spend their next session.
function PathForwardCard({ data, onNavigate }) {
  const moves = useMemo(() => composePathForward(data), [data]);

  if (moves.length === 0) {
    return (
      <Card label="The path forward">
        <Layer>
          <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.55, fontFamily: F.sans }}>
            No next moves surfaced. Either the project is in a holding pattern (waiting on prediction resolution dates) or no findings have actionable state yet.
          </div>
        </Layer>
      </Card>
    );
  }

  const story = `${moves.length} move${moves.length === 1 ? '' : 's'} ranked by leverage. The first is the move that, if completed in the next session, has the highest probability of unlocking a downstream methodology gate (Phase A coverage, hypothesis registration, prediction resolution).`;
  const takeaway = 'The methodology compounds: every completed move makes the next one more defensible. Conversely, leaving a Phase A unrun on a substantive finding leaks unverified claims into every downstream artifact (paper outline, donor brief, talk).';
  const caveat = 'Ranking is heuristic — sample-size limits, external dependencies (data not yet available), or stakeholder timelines can flip the actual priority. Treat the ranking as suggestion, not directive.';

  return (
    <Card label="The path forward" onClick={() => onNavigate?.('findings')}>
      <Layer>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 4 }}>
          {moves.map((m, i) => <NextMoveRow key={m.id} move={m} rank={i + 1} onNavigate={onNavigate} />)}
        </div>
      </Layer>
      <LayerSection label="Story">{story}</LayerSection>
      <LayerSection label="Takeaway" accent={C.gold}>{takeaway}</LayerSection>
      <CaveatStrip text={caveat} />
    </Card>
  );
}

function NextMoveRow({ move, rank, onNavigate }) {
  const priorityColor = move.priority === 'high' ? C.terra
    : move.priority === 'medium' ? C.gold : C.dim;
  return (
    <div
      onClick={(e) => { e.stopPropagation(); if (move.target) onNavigate?.(move.target); }}
      style={{
        display: 'grid', gridTemplateColumns: '24px 8px 1fr auto', gap: 10,
        alignItems: 'baseline', padding: '8px 10px',
        background: 'rgba(255,255,255,.02)',
        border: `1px solid ${C.b1}`, borderRadius: 4,
        cursor: move.target ? 'pointer' : 'default',
        transition: 'background 0.12s, border-color 0.12s',
      }}
      onMouseEnter={(e) => {
        if (move.target) {
          e.currentTarget.style.background = 'rgba(255,255,255,.04)';
          e.currentTarget.style.borderColor = C.b2;
        }
      }}
      onMouseLeave={(e) => {
        if (move.target) {
          e.currentTarget.style.background = 'rgba(255,255,255,.02)';
          e.currentTarget.style.borderColor = C.b1;
        }
      }}
    >
      <span style={{
        fontFamily: F.mono, fontSize: 11, color: C.dim, letterSpacing: '0.04em',
      }}>
        {String(rank).padStart(2, '0')}
      </span>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', background: priorityColor,
        marginTop: 6,
      }} />
      <div>
        <div style={{
          fontSize: 12, color: C.text, fontFamily: F.sans, fontWeight: 500,
          lineHeight: 1.4,
        }}>
          {move.label}
        </div>
        <div style={{
          fontSize: 11, color: C.mid, fontFamily: F.sans, lineHeight: 1.5,
          marginTop: 3,
        }}>
          {move.why}
        </div>
      </div>
      <span style={{
        fontFamily: F.mono, fontSize: 9, color: priorityColor,
        letterSpacing: '0.06em', textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}>
        {move.priority}
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CARD 5 — WHAT WE STILL DON'T KNOW
// ═══════════════════════════════════════════════════════════
// Q: "What gaps in the methodology are blocking confident
//     claims, and which ones are within my control?"
// Honest accounting of pending work — distinguishes gaps that
// the researcher can act on vs gaps that depend on external
// data availability.
function WhatWeDontKnowCard({ data, onNavigate }) {
  const gaps = useMemo(() => composeGaps(data), [data]);
  const total = gaps.actionable.length + gaps.external.length;

  if (total === 0) {
    return (
      <Card label="What we still don't know">
        <Layer>
          <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.55, fontFamily: F.sans }}>
            No gaps surfaced. Either the methodology is fully complete (unlikely at this stage) or the gap detector hasn't enumerated all the pre-conditions yet. Treat with skepticism.
          </div>
        </Layer>
      </Card>
    );
  }

  return (
    <Card label={`What we still don't know (${total})`}>
      {gaps.actionable.length > 0 && (
        <LayerSection label={`Actionable gaps (${gaps.actionable.length})`} accent={C.gold}>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: 18, fontFamily: F.sans, fontSize: 12, lineHeight: 1.55, color: C.text }}>
            {gaps.actionable.map((g, i) => (
              <li key={i} style={{ marginBottom: 4 }}>{g}</li>
            ))}
          </ul>
        </LayerSection>
      )}
      {gaps.external.length > 0 && (
        <LayerSection label={`External dependencies (${gaps.external.length})`} accent={C.dim}>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: 18, fontFamily: F.sans, fontSize: 12, lineHeight: 1.55, color: C.mid }}>
            {gaps.external.map((g, i) => (
              <li key={i} style={{ marginBottom: 4 }}>{g}</li>
            ))}
          </ul>
        </LayerSection>
      )}

      <LayerSection label="Takeaway" accent={C.gold}>
        Gaps are the research backlog — they're features of the methodology being honest about its limits, not bugs. The actionable list above is the to-do; the external list is the calendar of waiting.
      </LayerSection>

      <CaveatStrip text="If a gap doesn't appear in this list, the gap-detector probably doesn't check for it yet. Absence of a listed gap is not evidence of completeness." />
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════
// CARD 6 — SOLUTION CHAINS
// ═══════════════════════════════════════════════════════════
// Q: "Where can findings I haven't yet verified borrow
//     defensibility from findings that already survived
//     adversarial review?"
// Cross-finding causal reasoning: "X survived Phase A and has
// prescription Y. Z is structurally similar (shared cluster /
// matching gap profile / shared concepts) but is currently
// [divergent / pending / unverified]. Try applying Y's shape
// to Z, adapted for [the divergence direction or pending
// data state]."
// This is the "methodology compounds" mechanism — turning
// surveyed findings into instructions for the next ones.
function SolutionChainsCard({ data, onNavigate }) {
  const chains = useMemo(
    () => findSolutionChains({
      comparisons: data.comparisons || [],
      dynamics: DYNAMICS,
      perTargetLimit: 1,
    }),
    [data.comparisons],
  );

  if (chains.length === 0) {
    return (
      <Card label="Solution chains">
        <Layer>
          <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.55, fontFamily: F.sans }}>
            No chains surfaced. Either no findings have survived Phase A yet (no defensible sources), or the unverified findings are too structurally distant from the verified ones to suggest a chain (no shared cluster + no significant concept overlap).
          </div>
        </Layer>
        <CaveatStrip text="Chain-finding uses field-shape similarity (cluster, gap profile, concept overlap). It will not catch deeper analogies that would require reading both findings' raw mechanism — those need manual surfacing." />
      </Card>
    );
  }

  return (
    <Card label={`Solution chains (${chains.length})`}>
      <Layer>
        <div style={{ fontSize: 12, color: C.mid, fontFamily: F.sans, lineHeight: 1.55, marginBottom: 4 }}>
          Each chain proposes that an unverified finding could borrow the defensibility (and the prescription shape) of a Phase-A-survived finding it's structurally similar to. Use as a research prior, not a confirmed claim.
        </div>
      </Layer>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
        {chains.slice(0, 5).map((ch, i) => (
          <ChainRow key={`${ch.source.id}-${ch.target.id}`} chain={ch} onNavigate={onNavigate} />
        ))}
      </div>

      <LayerSection label="Takeaway" accent={C.gold}>
        Chains are the methodology's compounding mechanism. A surveyed finding doesn't just describe one mechanism — it shapes the priors for every structurally-similar mechanism that comes after. Each verified source pulls multiple unverified targets up the defensibility ladder.
      </LayerSection>

      <CaveatStrip text="Chain-finding uses field-shape similarity (cluster, gap profile, concept overlap in human translation). It does not read raw mechanism content; deep analogies that would require interpreting prose still need manual surfacing. A high score is not evidence of mechanism transfer — it's a hypothesis worth testing." />
    </Card>
  );
}

function ChainRow({ chain, onNavigate }) {
  const { source, sourceDyn, target, targetDyn, score, factors, reasoning } = chain;
  const sourceColor = source.phase_a_status === 'survived-respecified' ? C.gold : C.sage;
  const targetVerdict = (target.verdict || '').toUpperCase();
  const targetColor = targetVerdict === 'DIVERGENT' ? C.terra : C.dim;

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onNavigate?.('findings'); }}
      style={{
        padding: '12px 14px',
        background: 'rgba(255,255,255,.02)',
        border: `1px solid ${C.b1}`,
        borderRadius: 5,
        cursor: 'pointer',
        transition: 'border-color 0.12s, background 0.12s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = C.b2;
        e.currentTarget.style.background = 'rgba(255,255,255,.035)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = C.b1;
        e.currentTarget.style.background = 'rgba(255,255,255,.02)';
      }}
    >
      {/* Source -> Target arrow row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        marginBottom: 10, fontFamily: F.sans, fontSize: 12,
      }}>
        <span style={{ color: sourceColor, fontWeight: 600 }}>
          {sourceDyn.label}
        </span>
        <Pill color={sourceColor} text={source.phase_a_status === 'survived-respecified' ? 'SURVIVED · RESPECIFIED' : 'SURVIVED'} />
        <span style={{ color: C.dim, fontFamily: F.mono, fontSize: 14 }}>→</span>
        <span style={{ color: targetColor, fontWeight: 600 }}>
          {targetDyn.label}
        </span>
        {target.verdict && (
          <Pill color={targetColor} text={targetVerdict} />
        )}
        {!target.phase_a_status && target.verdict && target.verdict !== 'pending' && (
          <Pill color={C.amber} text="PHASE A NOT RUN" />
        )}
        <span style={{ marginLeft: 'auto', fontFamily: F.mono, fontSize: 9, color: C.dim, letterSpacing: '0.04em' }}>
          score {score}
        </span>
      </div>

      {/* Why these are similar */}
      <div style={{
        marginBottom: 10,
      }}>
        <div style={{
          fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
          color: C.dim, textTransform: 'uppercase', marginBottom: 5, fontWeight: 600,
        }}>
          Structural similarity
        </div>
        <ul style={{
          margin: 0, paddingLeft: 16,
          fontFamily: F.sans, fontSize: 11, color: C.mid, lineHeight: 1.55,
        }}>
          {factors.map((f, i) => (
            <li key={i} style={{ marginBottom: 2 }}>
              {f.label} <span style={{ color: C.dim, fontFamily: F.mono, fontSize: 9 }}>+{f.weight}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* The chain prescription */}
      <LayerSection label="Suggested chain" accent={C.sage}>
        Apply <strong style={{ color: C.text, fontWeight: 600 }}>{sourceDyn.label}</strong>'s prescription
        ({sourceDyn.ecological_prescription
          ? truncate(sourceDyn.ecological_prescription.split(/(?<=[.!?])\s/)[0], 100)
          : '(prescription pending)'})
        as a starting prior for <strong style={{ color: C.text, fontWeight: 600 }}>{targetDyn.label}</strong>.
        <br /><br />
        <em style={{ color: C.mid }}>{reasoning.adaptation}</em>
      </LayerSection>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CARD 7 — TRACK RECORD (CALIBRATION)
// ═══════════════════════════════════════════════════════════
// Q: "Is the methodology actually calibrated? What's the hit
//     rate of past predictions, and how soon will I find out?"
// The calibration loop is the methodology's claim to
// trustworthiness — without resolved predictions, confidence
// labels are unverifiable.
function TrackRecordCard({ data, onNavigate }) {
  const predictions = data.predictions || [];
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const pending = predictions.filter(p => p.status === 'pending');
  const due = pending.filter(p => new Date(p.resolution_date) <= today);
  const resolved = predictions.filter(p =>
    p.status?.startsWith('resolved') || p.status === 'inconclusive'
  );
  const confirmed = predictions.filter(p => p.status === 'resolved-true');
  const refuted = predictions.filter(p => p.status === 'resolved-false');

  const upcoming = pending
    .filter(p => new Date(p.resolution_date) > today)
    .slice()
    .sort((a, b) => new Date(a.resolution_date) - new Date(b.resolution_date))[0];

  const story = composeTrackRecordStory(pending.length, due.length, resolved.length, upcoming);
  const takeaway = composeTrackRecordTakeaway(predictions.length, resolved.length, confirmed.length, refuted.length);
  const action = composeTrackRecordAction(due, upcoming);
  const caveat = composeTrackRecordCaveat(predictions.length, resolved.length);

  return (
    <Card label="Track record (calibration)" onClick={() => onNavigate?.('predictions')}>
      <Layer>
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'baseline',
          marginBottom: 10,
        }}>
          <BreakStat color={C.gold}  count={pending.length}  label="pending" />
          {due.length > 0 && <BreakStat color={C.amber} count={due.length} label="due" />}
          <BreakStat color={C.sage}  count={resolved.length} label="resolved" />
        </div>
        {predictions.length > 0 && (
          <div style={{
            fontSize: 11, color: C.mid, fontFamily: F.sans, lineHeight: 1.55,
          }}>
            Hit rate: {resolved.length === 0 ? '—' : `${Math.round((confirmed.length / resolved.length) * 100)}% (n=${resolved.length})`}
            {upcoming && (
              <>
                {' · '}
                Next resolution: {formatDate(upcoming.resolution_date)} (
                {DYN_BY_KEY[upcoming.dynamic_key]?.label || upcoming.dynamic_key}
                )
              </>
            )}
          </div>
        )}
      </Layer>

      <LayerSection label="Story">{story}</LayerSection>
      <LayerSection label="Takeaway" accent={C.gold}>{takeaway}</LayerSection>

      {action && (
        <LayerAction
          accent={action.accent || C.sage}
          label={action.label}
          why={action.why}
          onClick={(e) => { e.stopPropagation(); onNavigate?.(action.target); }}
        />
      )}

      {caveat && <CaveatStrip text={caveat} />}
    </Card>
  );
}

// ─── Compose helpers — Card 3 (Where things break) ────────────
function composeBreaksStory(divergent, killed, respecified) {
  const parts = [];
  if (divergent.length > 0) {
    const top = divergent.slice().sort((a, b) =>
      Math.abs(Number(b.effect_size) || 0) - Math.abs(Number(a.effect_size) || 0))[0];
    const dyn = DYN_BY_KEY[top.dynamic_key];
    const dynLabel = dyn?.label || top.dynamic_key;
    parts.push(`The largest divergence is ${dynLabel} (d=${fmt(top.effect_size, 2)}).`);
  }
  if (killed.length > 0) {
    parts.push(`Phase A killed ${killed.length} claim${killed.length === 1 ? '' : 's'} — those are evidence against, not null results.`);
  }
  if (respecified.length > 0) {
    parts.push(`${respecified.length} finding${respecified.length === 1 ? '' : 's'} survived Phase A only after the claim was narrowed (respecification) — the original framing didn't hold; the weaker version did.`);
  }
  return parts.join(' ') || 'No active breaks.';
}

function composeBreaksTakeaway(divergent, killed) {
  if (divergent.length === 0 && killed.length === 0) {
    return 'Without breaks, the methodology has no domain-specificity claims. That\'s not a strength — it\'s a sign the test set is too coarse to surface where the parallel doesn\'t hold.';
  }
  return 'Breaks are information, not failure. They constrain the universality claim and surface where the parallel needs domain-specific framing. Each unreviewed break is a candidate for either substantive divergence (real mechanism difference) or operationalization mismatch (artifact). Phase A distinguishes those two.';
}

function composeBreaksAction(divergent, killed) {
  const unreviewed = divergent.filter(c => !c.phase_a_status);
  if (unreviewed.length === 0) return null;
  const top = unreviewed.slice().sort((a, b) =>
    Math.abs(Number(b.effect_size) || 0) - Math.abs(Number(a.effect_size) || 0))[0];
  const dyn = DYN_BY_KEY[top.dynamic_key];
  const dynLabel = dyn?.label || top.dynamic_key;
  return {
    label: `Run Phase A on ${dynLabel}`,
    why: `Largest unreviewed divergence (d=${fmt(top.effect_size, 2)}). Phase A will distinguish real mechanism difference from operationalization artifact. Highest leverage in the break-set.`,
    target: 'findings',
  };
}

function composeBreaksCaveat(divergent) {
  const noPhaseA = divergent.filter(c => !c.phase_a_status).length;
  if (noPhaseA === 0) return 'Every divergent finding has been Phase A red-teamed — these breaks are defensible.';
  return `${noPhaseA} divergent finding${noPhaseA === 1 ? ' has' : 's have'} not been Phase A red-teamed. Treat as suggestive of domain-specificity, not as confirmed evidence — the divergence may reflect operationalization rather than mechanism.`;
}

// ─── Compose helpers — Card 4 (Path forward) ──────────────────
function composePathForward(data) {
  const comps = data.comparisons || [];
  const hypotheses = data.hypotheses || [];
  const predictions = data.predictions || [];
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const moves = [];

  // 1. Due predictions — highest urgency (resolution date passed)
  const duePredictions = predictions.filter(p =>
    p.status === 'pending' && new Date(p.resolution_date) <= today
  );
  for (const p of duePredictions) {
    moves.push({
      id: `resolve-pred-${p.id}`,
      priority: 'high',
      label: `Resolve due prediction: ${truncate(p.claim, 60)}`,
      why: `Resolution date (${formatDate(p.resolution_date)}) has passed. Mark confirmed / refuted / inconclusive based on current evidence. Each resolved prediction populates the calibration registry.`,
      target: 'predictions',
    });
  }

  // 2. Substantive findings without Phase A — biggest effect size first
  const substantive = comps.filter(isSubstantive);
  const noPhaseA = substantive.filter(c => !c.phase_a_status)
    .slice()
    .sort((a, b) => Math.abs(Number(b.effect_size) || 0) - Math.abs(Number(a.effect_size) || 0));
  for (const c of noPhaseA.slice(0, 2)) {
    const dyn = DYN_BY_KEY[c.dynamic_key];
    moves.push({
      id: `phase-a-${c.id}`,
      priority: 'high',
      label: `Run Phase A on ${dyn?.label || c.dynamic_key}`,
      why: `${c.verdict || 'Substantive finding'} with d=${fmt(c.effect_size, 2)} carries an unverified claim. ~30s + ~$0.20 per run.`,
      target: 'findings',
    });
  }

  // 3. Survived findings without registered hypothesis
  const hypByComp = new Set((hypotheses || []).map(h => h.comparison_id));
  const noHypothesis = substantive.filter(c =>
    c.phase_a_status && !hypByComp.has(c.id)
  );
  for (const c of noHypothesis.slice(0, 2)) {
    const dyn = DYN_BY_KEY[c.dynamic_key];
    moves.push({
      id: `register-hyp-${c.id}`,
      priority: 'medium',
      label: `Register parallel hypothesis for ${dyn?.label || c.dynamic_key}`,
      why: `Phase A survived with no hypothesis registered. The hypothesis is the structural claim being tested — without it, the finding has no falsifier on record.`,
      target: 'hypotheses',
    });
  }

  // 4. Pending dynamics (data not yet wired)
  const pending = comps.filter(c =>
    !c.verdict || ['pending', 'insufficient_data'].includes(String(c.verdict).toLowerCase())
  );
  if (pending.length > 0) {
    moves.push({
      id: 'wire-pending',
      priority: 'medium',
      label: `Wire data for ${pending.length} pending dynamic${pending.length === 1 ? '' : 's'}`,
      why: `Each pending dynamic is a finding pulled out of indeterminacy. Without observations on both sides, the comparison cannot be evaluated.`,
      target: 'simulation',
    });
  }

  // Sort + cap at 3
  const PRI = { high: 0, medium: 1, low: 2 };
  return moves
    .slice()
    .sort((a, b) => (PRI[a.priority] ?? 9) - (PRI[b.priority] ?? 9))
    .slice(0, 3);
}

// ─── Compose helpers — Card 5 (What we don't know) ────────────
function composeGaps(data) {
  const comps = data.comparisons || [];
  const hypotheses = data.hypotheses || [];
  const predictions = data.predictions || [];
  const questions = data.questions || [];

  const actionable = [];
  const external = [];

  const substantive = comps.filter(isSubstantive);
  const noPhaseA = substantive.filter(c => !c.phase_a_status);
  if (noPhaseA.length > 0) {
    const names = noPhaseA.map(c => DYN_BY_KEY[c.dynamic_key]?.label || c.dynamic_key);
    actionable.push(`${noPhaseA.length} substantive finding${noPhaseA.length === 1 ? '' : 's'} not Phase-A red-teamed: ${names.join(', ')}.`);
  }

  const hypByComp = new Set(hypotheses.map(h => h.comparison_id));
  const survivedNoHyp = substantive.filter(c => c.phase_a_status && !hypByComp.has(c.id));
  if (survivedNoHyp.length > 0) {
    const names = survivedNoHyp.map(c => DYN_BY_KEY[c.dynamic_key]?.label || c.dynamic_key);
    actionable.push(`${survivedNoHyp.length} Phase-A-survived finding${survivedNoHyp.length === 1 ? '' : 's'} without registered hypothesis: ${names.join(', ')}.`);
  }

  if (questions.length === 0) {
    actionable.push('Open questions register is empty — no surfaced unknowns documented for future investigation.');
  }

  const predByComp = new Set(predictions.map(p => p.comparison_id));
  const survivedNoPred = substantive.filter(c =>
    (c.phase_a_status === 'survived' || c.phase_a_status === 'survived-respecified') &&
    !predByComp.has(c.id)
  );
  if (survivedNoPred.length > 0) {
    const names = survivedNoPred.map(c => DYN_BY_KEY[c.dynamic_key]?.label || c.dynamic_key);
    actionable.push(`${survivedNoPred.length} survived finding${survivedNoPred.length === 1 ? '' : 's'} without forward prediction: ${names.join(', ')}.`);
  }

  const pendingDynamics = comps.filter(c =>
    !c.verdict || ['pending', 'insufficient_data'].includes(String(c.verdict).toLowerCase())
  );
  if (pendingDynamics.length > 0) {
    const names = pendingDynamics.map(c => DYN_BY_KEY[c.dynamic_key]?.label || c.dynamic_key);
    external.push(`${pendingDynamics.length} dynamic${pendingDynamics.length === 1 ? '' : 's'} pending data on one or both sides: ${names.join(', ')}.`);
  }

  // Predictions waiting on resolution dates are external (calendar-bound)
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const futurePredictions = predictions.filter(p =>
    p.status === 'pending' && new Date(p.resolution_date) > today
  );
  if (futurePredictions.length > 0) {
    const earliest = futurePredictions
      .slice()
      .sort((a, b) => new Date(a.resolution_date) - new Date(b.resolution_date))[0];
    external.push(`${futurePredictions.length} forward prediction${futurePredictions.length === 1 ? '' : 's'} awaiting resolution date (earliest: ${formatDate(earliest.resolution_date)}).`);
  }

  return { actionable, external };
}

// ─── Compose helpers — Card 7 (Track record) ──────────────────
function composeTrackRecordStory(pending, due, resolved, upcoming) {
  if (pending + resolved === 0) {
    return 'No predictions registered yet. The track record is empty — calibration begins when predictions are logged with confidence labels and resolution dates.';
  }
  if (resolved === 0 && due === 0) {
    return `${pending} prediction${pending === 1 ? '' : 's'} pending; the calibration registry is in build-up phase. Hit rates only become statistically meaningful at n ≥ 10 resolved per confidence tier${upcoming ? `; the first will resolve ${formatDate(upcoming.resolution_date)}` : ''}.`;
  }
  if (due > 0) {
    return `${due} prediction${due === 1 ? ' has' : 's have'} reached its resolution date and ${due === 1 ? 'awaits' : 'await'} a verdict. Resolving promptly preserves the integrity of the calibration record (waiting too long lets hindsight bias creep in).`;
  }
  return `${resolved} resolved · ${pending} still pending. The track record is starting to take shape.`;
}

function composeTrackRecordTakeaway(total, resolved, confirmed, refuted) {
  if (total === 0) {
    return 'A methodology without forward predictions has no falsification record — every claim is post-hoc explanation. Logging predictions transforms claims into testable bets.';
  }
  if (resolved === 0) {
    return 'The calibration claim is a future commitment, not a current fact. Confidence labels (high/med-high/med-low/low) are research promises that must be redeemed by resolution dates.';
  }
  if (resolved < 10) {
    return `${resolved} resolved prediction${resolved === 1 ? ' is' : 's are'} not enough to claim calibration — at this n, hit rates are anecdotes. The track record becomes evidence at n ≥ 10 per confidence tier.`;
  }
  // n >= 10 — actually meaningful
  const pct = Math.round((confirmed / resolved) * 100);
  return `${pct}% hit rate across ${resolved} resolved predictions. ${confirmed} confirmed, ${refuted} refuted. This is now an actual calibration claim, not a promise.`;
}

function composeTrackRecordAction(due, upcoming) {
  if (due.length > 0) {
    return {
      label: `Resolve ${due.length} due prediction${due.length === 1 ? '' : 's'}`,
      why: 'Resolution dates have passed. Mark confirmed / refuted / inconclusive in the Predictions tab — each resolution populates the calibration record.',
      target: 'predictions',
      accent: C.amber,
    };
  }
  if (upcoming) {
    const days = Math.round((new Date(upcoming.resolution_date) - new Date()) / 86400000);
    if (days <= 30) {
      return {
        label: `First resolution upcoming in ${days} day${days === 1 ? '' : 's'}`,
        why: `Resolution date ${formatDate(upcoming.resolution_date)}. Begin gathering evidence now — last-minute resolution is more likely to flatten nuance into a binary verdict.`,
        target: 'predictions',
        accent: C.gold,
      };
    }
  }
  return null;
}

function composeTrackRecordCaveat(total, resolved) {
  if (total === 0) {
    return 'Calibration cannot begin without predictions. Until then, every confidence label is unanchored — it predicts no specific verdict at any specific time.';
  }
  if (resolved < 10) {
    return `Calibration is in build-up phase (n=${resolved}). Treat hit-rate displays as placeholders. Statistical interpretation requires n ≥ 10 resolved per tier.`;
  }
  return `Hit rate is the methodology's track record — but selection effects matter. Predictions registered after-the-fact, vague resolution criteria, or motivated reasoning at resolution time can all produce inflated calibration. Audit the criteria as carefully as the outcomes.`;
}

// ─── Compose helpers — Card 1 (Where the research stands) ─────
function composeStandsStory(buckets, substantive) {
  const parts = [];
  const par = buckets.PARALLEL.length;
  const near = buckets['NEAR-PARALLEL'].length;
  const div = buckets.DIVERGENT.length;
  const pend = buckets.pending.length + buckets.insufficient_data.length;

  if (substantive.length === 0) {
    return 'No substantive findings yet — most dynamics are still pending data on one or both sides.';
  }

  if (par > 0) parts.push(`${par} confirmed parallel${par === 1 ? '' : 's'}`);
  if (near > 0) parts.push(`${near} near-parallel${near === 1 ? '' : 's'}`);
  if (div > 0) parts.push(`${div} divergent finding${div === 1 ? '' : 's'}`);
  const headlinePart = parts.join(', ');

  let body = `${capitalize(headlinePart)}. `;
  if (pend > 0) body += `${pend} ${pend === 1 ? 'dynamic remains' : 'dynamics remain'} pending. `;

  // Find the strongest finding for narrative anchor
  const strongest = pickStrongest(substantive);
  if (strongest) {
    const dyn = DYN_BY_KEY[strongest.dynamic_key];
    const dynLabel = dyn?.label || strongest.dynamic_key;
    if (strongest.phase_a_status === 'survived-respecified') {
      body += `The most defensible finding (${dynLabel}) survived Phase A in respecified form — meaning the methodology forced a sharper, more falsifiable claim before crediting the result.`;
    } else if (strongest.phase_a_status === 'survived') {
      body += `The most defensible finding (${dynLabel}) cleared Phase A intact.`;
    } else {
      body += `The most defensible finding (${dynLabel}) has not yet been Phase A red-teamed.`;
    }
  }
  return body.trim();
}

function composeStandsTakeaway(buckets, substantive) {
  if (substantive.length === 0) return 'Wait for findings before drawing conclusions.';
  const par = buckets.PARALLEL.length;
  const near = buckets['NEAR-PARALLEL'].length;
  const div = buckets.DIVERGENT.length;
  if (par + near > 0 && div > 0) {
    return 'The methodology is producing a heterogeneous picture — neither uniform parallel nor uniform divergence. That heterogeneity is itself informative: it tells us *which* mechanisms transfer across domains and which do not. The publishable claim is not "systems are similar" but "specific structural mechanisms produce similar distributions; others produce domain-specific differences."';
  }
  if (par + near > 0 && div === 0) {
    return `All ${par + near} substantive findings show parallel or near-parallel distributions. Treat this as suggestive but not conclusive until divergence-shaped findings emerge to provide contrast.`;
  }
  if (div > 0 && par + near === 0) {
    return `All ${div} substantive findings diverge. The claim is not that the parallel breaks — it's that *under current operationalization* the systems differ. Refining operationalization or restricting subsets may surface parallels masked at the full-sample level.`;
  }
  return 'Limited data; findings still consolidating.';
}

function composeStandsAction(comps, substantive, phaseACovered) {
  // Highest-leverage next move heuristic (ranked):
  // 1. Pending dynamics with no data → wire data
  // 2. Substantive finding without Phase A + biggest effect size → run Phase A
  // 3. Hypothesis registry empty → register hypotheses
  const pending = comps.filter(c =>
    !c.verdict || ['pending', 'insufficient_data'].includes(String(c.verdict).toLowerCase())
  );
  const substantiveWithoutPhaseA = substantive.filter(c => !c.phase_a_status);

  if (substantiveWithoutPhaseA.length > 0) {
    const target = substantiveWithoutPhaseA
      .slice()
      .sort((a, b) => Math.abs(Number(b.effect_size) || 0) - Math.abs(Number(a.effect_size) || 0))[0];
    const dyn = DYN_BY_KEY[target.dynamic_key];
    const dynLabel = dyn?.label || target.dynamic_key;
    return {
      label: `Run Phase A on ${dynLabel}`,
      why: `${substantiveWithoutPhaseA.length} substantive finding${substantiveWithoutPhaseA.length === 1 ? ' carries' : 's carry'} unverified claims. ${dynLabel} has the largest unreviewed effect (d=${fmt(target.effect_size, 2)}) — biggest leverage.`,
      target: 'findings',
    };
  }
  if (pending.length > 0) {
    return {
      label: `Wire data for ${pending.length} pending dynamic${pending.length === 1 ? '' : 's'}`,
      why: `Findings can't be evaluated without observations. Each wired source is a finding pulled out of indeterminacy.`,
      target: 'simulation',
    };
  }
  return null;
}

function composeStandsCaveat(substantive, phaseACovered) {
  if (substantive.length === 0) return null;
  if (substantive.length === phaseACovered) return null;
  const remaining = substantive.length - phaseACovered;
  return `${remaining} substantive finding${remaining === 1 ? '' : 's'} not yet Phase-A red-teamed — those claims carry elevated interpretive caution until reviewed.`;
}

// ─── Compose helpers — Card 2 (Strongest threads) ─────────────
function pickStrongestThreads(comps, hypotheses, predictions, limit = 3) {
  const substantive = comps.filter(isSubstantive);
  const ranked = substantive
    .slice()
    .sort((a, b) => threadScore(b) - threadScore(a))
    .slice(0, limit);

  const hypByComp = new Map();
  for (const h of hypotheses) {
    if (!hypByComp.has(h.comparison_id) ||
        new Date(h.registered_at) > new Date(hypByComp.get(h.comparison_id).registered_at)) {
      hypByComp.set(h.comparison_id, h);
    }
  }
  const predByComp = new Map();
  for (const p of predictions) {
    const cur = predByComp.get(p.comparison_id);
    if (!cur || new Date(p.resolution_date) < new Date(cur.resolution_date)) {
      predByComp.set(p.comparison_id, p);
    }
  }

  return ranked.map(comp => {
    const dyn = DYN_BY_KEY[comp.dynamic_key];
    const hypothesis = hypByComp.get(comp.id) || null;
    const prediction = predByComp.get(comp.id) || null;
    return {
      comp,
      dyn,
      hypothesis,
      prediction,
      deviationPoint: composeDeviationPoint(comp, dyn),
      takeaway: composeThreadTakeaway(comp, dyn),
      intervention: composeIntervention(dyn),
      caveat: composeThreadCaveat(comp, dyn),
    };
  });
}

function threadScore(comp) {
  let score = 0;
  if (comp.phase_a_status === 'survived')             score += 100;
  else if (comp.phase_a_status === 'survived-respecified') score += 80;
  else if (comp.phase_a_status === 'killed')          score += 30;
  if ((comp.confidence || '').toUpperCase() === 'HIGH')   score += 50;
  if ((comp.confidence || '').toUpperCase() === 'MEDIUM') score += 30;
  if ((comp.confidence || '').toUpperCase() === 'LOW')    score += 10;
  // small bias toward larger samples
  const n = (Number(comp.eco_n) || 0) + (Number(comp.human_n) || 0);
  score += Math.min(20, Math.log10(Math.max(1, n)) * 4);
  return score;
}

function composeDeviationPoint(comp, dyn) {
  // The "deviation point" is the inflection — where this finding
  // departed from the null/expected pattern. We pull it from
  // structured fields when available (caveat, finding_text snippet,
  // Phase A residual_caveat).
  const verdict = (comp.verdict || '').toUpperCase();
  const dynLabel = dyn?.label || comp.dynamic_key;
  const respec = comp.phase_a_modules?.counter_narrative?.residual_caveat;

  if (respec) {
    return `Phase A respec: ${respec}`;
  }
  if (verdict === 'PARALLEL' || verdict === 'NEAR-PARALLEL') {
    if (Math.abs(Number(comp.effect_size) || 0) < 0.1) {
      return `${dynLabel} ratios are statistically indistinguishable across domains at the full-sample level (d=${fmt(comp.effect_size, 3)}). The deviation point is whether this similarity holds under restricted subsets — extant-only filters, methodology-matched cohorts, or independent dataset replication.`;
    }
    return `${dynLabel} shows a small but real cross-domain gap (d=${fmt(comp.effect_size, 2)}). The deviation point is whether the magnitude is structural or measurement-driven.`;
  }
  if (verdict === 'DIVERGENT') {
    const sign = (Number(comp.effect_size) || 0) > 0 ? 'higher' : 'lower';
    const eco = sign === 'higher' ? 'ecological' : 'human';
    return `${dynLabel} diverges meaningfully (d=${fmt(comp.effect_size, 2)}); ${eco} systems show the larger ratio. The deviation point is whether this reflects genuine mechanism difference or operationalization mismatch.`;
  }
  return `${dynLabel} has not yet produced a verdict.`;
}

function composeThreadTakeaway(comp, dyn) {
  if (comp.phase_a_status === 'survived-respecified') {
    return 'Survived adversarial review in respecified form. The respec narrowed the claim — that\'s the methodology working as designed (precision over breadth). Treat the respecified version as the load-bearing claim, not the original.';
  }
  if (comp.phase_a_status === 'survived') {
    return 'Survived adversarial review intact. Defensible to cite externally with the standard caveats about sample size and dataset coverage.';
  }
  if (comp.phase_a_status === 'killed') {
    return 'Phase A killed the finding. Treat as evidence against the claim, not as a null result — the methodology actively refuted it.';
  }
  // No Phase A yet
  const verdict = (comp.verdict || '').toUpperCase();
  if (verdict === 'DIVERGENT') {
    return 'Statistically robust divergence, but no adversarial review yet. Phase A may surface that the divergence reflects operationalization, not mechanism — treat as suggestive until tested.';
  }
  return 'Awaiting Phase A red-team. Numbers are clean but not yet stress-tested.';
}

function composeIntervention(dyn) {
  if (!dyn) return null;
  const eco = dyn.ecological_prescription;
  const human = dyn.human_translation;
  if (!eco && !human) return null;
  if (eco && human) {
    // Take the first-sentence summary of each, pair them
    const ecoLine = eco.split(/(?<=[.!?])\s/)[0];
    return `${ecoLine} The human-system parallel applies the same intervention shape to the analogous threshold/cascade.`;
  }
  return eco || human;
}

function composeThreadCaveat(comp, dyn) {
  const parts = [];
  const ecoN = Number(comp.eco_n) || 0;
  const humanN = Number(comp.human_n) || 0;
  if (humanN < 100) parts.push(`human-side n=${humanN} is small`);
  else if (ecoN < 100 && ecoN > 0) parts.push(`eco-side n=${ecoN} is small`);
  if (!comp.phase_a_status) parts.push('Phase A not yet run');
  if (comp.caveat) parts.push(comp.caveat);
  if (parts.length === 0) return null;
  return parts.join(' · ');
}

// ─── Shared helpers ───────────────────────────────────────────
function isSubstantive(c) {
  return c.verdict && !['pending', 'insufficient_data', 'error'].includes(String(c.verdict).toLowerCase());
}

function groupByVerdict(comps) {
  const out = {
    PARALLEL: [], 'NEAR-PARALLEL': [], DIVERGENT: [],
    insufficient_data: [], pending: [], error: [], other: [],
  };
  for (const c of comps) {
    const v = c.verdict || 'pending';
    const upper = String(v).toUpperCase();
    if (out[upper]) out[upper].push(c);
    else if (out[v]) out[v].push(c);
    else out.other.push(c);
  }
  return out;
}

function pickStrongest(substantive) {
  if (substantive.length === 0) return null;
  return substantive.slice().sort((a, b) => threadScore(b) - threadScore(a))[0];
}

function fmt(n, digits = 4) {
  if (n === null || n === undefined) return '—';
  if (typeof n !== 'number') return String(n);
  if (Math.abs(n) >= 10000) return n.toExponential(2);
  return n.toFixed(digits);
}
function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
}
function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
function capitalize(s) {
  if (!s) return s;
  return s[0].toUpperCase() + s.slice(1);
}

// ═══════════════════════════════════════════════════════════
// CARD SHELL + LAYER PRIMITIVES
// ═══════════════════════════════════════════════════════════
// Pattern: micro-label · 5 layers · caveat strip.
function Card({ label, onClick, children }) {
  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        padding: '14px 18px 14px 18px',
        background: 'rgba(255,255,255,.02)',
        border: `1px solid ${C.b1}`, borderRadius: 6,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.12s, background 0.12s',
        display: 'flex', flexDirection: 'column',
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.borderColor = C.b2;
          e.currentTarget.style.background = 'rgba(255,255,255,.035)';
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.borderColor = C.b1;
          e.currentTarget.style.background = 'rgba(255,255,255,.02)';
        }
      }}
    >
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
        color: C.dim, textTransform: 'uppercase', marginBottom: 12,
        fontWeight: 600,
      }}>
        ◆ {label}
      </div>
      {children}
    </div>
  );
}

// Layer with no label — the headline / read layer.
function Layer({ children }) {
  return <div>{children}</div>;
}

// Layer with a small label above. Used for Story / Takeaway /
// Deviation point / Intervention. Keeps the layered structure
// visible while not overpowering the prose.
function LayerSection({ label, accent, children }) {
  if (children == null || children === '') return null;
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${C.b1}` }}>
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
        color: accent || C.dim, textTransform: 'uppercase',
        marginBottom: 5, fontWeight: 600,
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: F.sans, fontSize: 12, lineHeight: 1.55, color: C.text,
      }}>
        {children}
      </div>
    </div>
  );
}

// Action layer — clickable strip with a label + rationale.
// Used for the suggested next move and for prediction-tied
// "tested by" links. The accent color signals the action class
// (sage = do this now, purple = scheduled / tracked).
function LayerAction({ accent, label, why, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        marginTop: 12, padding: '10px 12px',
        background: `${accent}10`,
        border: `1px solid ${accent}40`, borderRadius: 4,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background 0.12s',
      }}
      onMouseEnter={(e) => {
        if (onClick) e.currentTarget.style.background = `${accent}18`;
      }}
      onMouseLeave={(e) => {
        if (onClick) e.currentTarget.style.background = `${accent}10`;
      }}
    >
      <div style={{
        fontFamily: F.mono, fontSize: 9, letterSpacing: '0.06em',
        color: accent, fontWeight: 600, marginBottom: 4,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span>→</span>
        <span>{label}</span>
      </div>
      <div style={{
        fontFamily: F.sans, fontSize: 11, color: C.mid, lineHeight: 1.5,
      }}>
        {why}
      </div>
    </div>
  );
}

// Caveat strip — always at the bottom of the card, italic,
// small, separated by dashed border. Non-optional per the
// two-lens rule (cards without caveats are tells).
function CaveatStrip({ text }) {
  return (
    <div style={{
      marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${C.b1}`,
      fontFamily: F.sans, fontSize: 10, color: C.dim, lineHeight: 1.5,
      fontStyle: 'italic',
    }}>
      ↳ {text}
    </div>
  );
}

function Pill({ color, text }) {
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 3,
      border: `1px solid ${color}60`,
      background: `${color}15`, color,
      letterSpacing: '0.08em', fontWeight: 600, textTransform: 'uppercase',
      fontFamily: 'inherit', fontSize: 'inherit',
    }}>
      {text}
    </span>
  );
}
